/* eslint-disable prefer-rest-params */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { isBrowser } from './isBrowser';

export function debounce(
  func: (...args: any[]) => void,
  wait: number,
  immediate?: boolean
): (...args: any[]) => void {
  if (!isBrowser()) {
    return () => {
      throw Error('debounce: function was not invoked in browser');
    };
  }
  let timeout: number | undefined;
  return function (..._: any[]) {
    const later = () => {
      timeout = undefined;
      if (!immediate) {
        func.apply(this, arguments);
      }
    };
    const callNow = immediate && !timeout;
    window.clearTimeout(timeout as number);
    timeout = window.setTimeout(later, wait);
    if (callNow) {
      func.apply(this, arguments);
    }
  };
}

/**
 *
 * Same as debounce, but for functions that return promises and
 * need to be able to chain .then(...) on the returning function
 *
 * @param func the promise returning function to debounce
 * @param wait how long to wait in ms
 * @param immediate if the function should be called immediate or not
 */
export function debounceP<T = any>(
  func: (...args: any[]) => Promise<T>,
  wait: number,
  immediate?: boolean
): (...args: any[]) => Promise<T> {
  if (!isBrowser()) {
    return () => Promise.reject('debounceP: function was not invoked in browser');
  }

  let timeout: number | undefined;
  let value: T;
  const resolves: ((value?: any) => void)[] = [];

  return function (...args: any[]) {
    return new Promise(resolve => {
      const later = () => {
        timeout = undefined;
        const res = immediate ? value : (func.apply(this, args) as T);
        resolves.forEach(r => r(res));
      };

      const callNow = immediate && !timeout;

      window.clearTimeout(timeout as number);
      timeout = window.setTimeout(later, wait);

      if (callNow) {
        value = func.apply(this, args) as T;
        resolve(value);
      } else {
        resolves.push(resolve);
      }
    });
  };
}
